import './App.css';
import React, { useState, useEffect } from 'react';
import Login from './components/Login';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import { useAuth } from './context/AuthContext';
import Navbar from './components/Navbar';
import CharacterPromptBanner from './components/Character/CharacterPromptBanner';
import CharacterCreationModal from './components/Character/CharacterCreationModal';
import CreatePost from './components/TownSquare/CreatePost';
import TownSquare from './components/TownSquare/TownSquare';
import PostView from './components/PostView';
import EditPost from './components/TownSquare/EditPost';
import MyCharactersPage from './components/Character/MyCharactersPage';
import CharacterDetailPage from './components/Character/CharacterDetailPage';
import Dashboard from './components/Dashboard';
import SchedulingPrefs from './components/SchedulingPrefs';
import CampaignScheduleView from './components/Campaign/CampaignScheduleView';
import { getCharactersByUserId } from './services/characterService';
import { initializeEmailJS } from './services/emailService';
import FileUpload from './components/Character/FileUpload';
import UserFilesList from './components/UserFilesList';
import BennyJournalView from './components/BennyJournal/BennyJournalDisplay';
import CreateJournalEntry from './components/BennyJournal/CreateJournalEntry';
import EditJournalEntry from './components/BennyJournal/EditJournalEntry';
import PdfViewerPage from './components/BennyJournal/PdfViewerPage';
import CreateCampaignEpisode from './components/Campaign/CreateCampaignEpisode';
import CampaignEpisodesList from './components/Campaign/CampaignEpisodesList';
import CampaignEpisodeDetailPage from './components/Campaign/CampaignEpisodeDetailPage';
import BennyJournalEntryView from './components/BennyJournal/BennyJournalEntryView';
import AccountPage from './components/AccountPage';
import TrophyHall from './components/TrophyHall/TrophyHall';
import MapsPage from './components/Maps/MapsPage';
import LeafletMapViewer from './components/Maps/LeafletMapViewer';
import AdminPanel from './components/AdminPanel';
import SearchResultsPage from './components/SearchResultsPage';
import WikiPage from './components/Wiki/WikiPage';
import OverallRelationshipGraph from './components/Wiki/Common/OverallRelationshipGraph';

/**
 * Home component that renders the main dashboard
 * @param {Object} characters - Array of user's characters
 * @param {boolean} loadingCharacters - Loading state for characters
 */
const Home = ({ characters, loadingCharacters }) => {
  if (loadingCharacters) {
    return (
      <div className="App">
        <header className="App-header">
          <p>Loading characters...</p>
        </header>
      </div>
    );
  }

  return (
    <div className="App">
      <Dashboard />
    </div>
  );
};

function App() {
  const { firestoreUser, isUltraAuth } = useAuth();
  const [characters, setCharacters] = useState([]);
  const [loadingCharacters, setLoadingCharacters] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Initialize EmailJS once when the app loads
  useEffect(() => {
    initializeEmailJS();
  }, []);

  useEffect(() => {
    const fetchCharacters = async () => {
      if (firestoreUser && isUltraAuth) {
        setLoadingCharacters(true);
        try {
          const userCharacters = await getCharactersByUserId(firestoreUser.uid);
          setCharacters(userCharacters);
        } catch (error) {
          console.error("Failed to fetch characters:", error);
          setCharacters([]); // Set to empty on error
        } finally {
          setLoadingCharacters(false);
        }
      } else {
        setCharacters([]); // Clear characters if no user or not ultra authenticated
        setLoadingCharacters(false);
      }
    };

    fetchCharacters();
  }, [firestoreUser, isUltraAuth]);

  const handleOpenCreateModal = () => setShowCreateModal(true);
  const handleCloseCreateModal = () => setShowCreateModal(false);

  /**
   * Refreshes the user's character list from the database
   * Also closes character creation modal if open
   */
  const refreshCharacters = async () => {
    if (firestoreUser) {
      setLoadingCharacters(true);
      try {
        const userCharacters = await getCharactersByUserId(firestoreUser.uid);
        setCharacters(userCharacters);
      } catch (error) {
        console.error("Failed to refetch characters:", error);
      } finally {
        setLoadingCharacters(false);
      }
    }
    handleCloseCreateModal(); 
  };

  /**
   * Determines if character creation banner should be shown
   * Shows when user is authenticated but has no characters
   */
  const shouldShowBanner = firestoreUser && isUltraAuth && !loadingCharacters && characters.length === 0;

  return (
    <>
      <Navbar />
      {shouldShowBanner && (
        <CharacterPromptBanner onOpenCreateCharacterModal={handleOpenCreateModal} />
      )}        <CharacterCreationModal
        open={showCreateModal}
        handleClose={handleCloseCreateModal}
        onCharacterCreated={refreshCharacters}
      />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Home characters={characters} loadingCharacters={loadingCharacters} />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/dashboard"
          element={ 
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/search-results"
          element={ 
            <ProtectedRoute>
              <SearchResultsPage />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/town-square"
          element={
            <ProtectedRoute>
              <TownSquare />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/post/:postId"
          element={
            <ProtectedRoute>
              <PostView />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/post/:postId/edit"
          element={
            <ProtectedRoute>
              <EditPost />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/my-characters"
          element={
            <ProtectedRoute>
              <MyCharactersPage 
                characters={characters} 
                loadingCharacters={loadingCharacters} 
                onCharacterUpdate={refreshCharacters} // Pass refresh function
              />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/character/:characterId"
          element={
            <ProtectedRoute>
              <CharacterDetailPage 
                characters={characters} 
                loadingCharacters={loadingCharacters} 
                onCharacterUpdate={refreshCharacters} // Pass refresh function
              />
            </ProtectedRoute>
          }
        />
        <Route
          path="/create-post"
          element={
            <ProtectedRoute>
              <CreatePost />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/scheduling-prefs"
          element={
            <ProtectedRoute>
              <SchedulingPrefs />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/campaign-schedule"
          element={
            <ProtectedRoute>
              <CampaignScheduleView />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/upload"
          element={
            <ProtectedRoute>
              <FileUpload />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/my-files" // New route for listing files
          element={
            <ProtectedRoute>
              <UserFilesList />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/benny-journal/new"
          element={
            <ProtectedRoute>
              <CreateJournalEntry />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/benny-journal/:entryId/edit"
          element={
            <ProtectedRoute>
              <EditJournalEntry />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/benny-journal/:entryId"
          element={
            <ProtectedRoute>
              <BennyJournalEntryView />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/benny-journal"
          element={ 
            <ProtectedRoute>
              <BennyJournalView />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/campaign-episodes/create"
          element={
            <ProtectedRoute>
              <CreateCampaignEpisode />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/campaign-episodes/:episodeId"
          element={
            <ProtectedRoute>
              <CampaignEpisodeDetailPage userCharacters={characters} />
            </ProtectedRoute>
          }
        />
        <Route 
          path="/campaign-episodes"
          element={
            <ProtectedRoute>
              <CampaignEpisodesList />
            </ProtectedRoute>
          }
        />
        <Route path="/view-pdf/:entryId" element={<ProtectedRoute><PdfViewerPage /></ProtectedRoute>} />
        <Route path="/view-pdf" element={<ProtectedRoute><PdfViewerPage /></ProtectedRoute>} />
        <Route 
          path="/account" 
          element={
            <ProtectedRoute>
              <AccountPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/trophy-hall" 
          element={
            <ProtectedRoute>
              <TrophyHall />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/maps" 
          element={
            <ProtectedRoute>
              <MapsPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/maps/:mapId" 
          element={
            <ProtectedRoute>
              <LeafletMapViewer />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin" 
          element={
            <ProtectedRoute>
              <AdminPanel />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/wiki" 
          element={
            <ProtectedRoute>
              <WikiPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/wiki/:entryId" 
          element={
            <ProtectedRoute>
              <WikiPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/wiki/relationship-graph" 
          element={
            <ProtectedRoute>
              <OverallRelationshipGraph />
            </ProtectedRoute>
          } 
        />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </>
  );
}

export default App;
